import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Phone, Zap, Clock, TrendingUp, Users, Shield, MessageSquare, CheckCircle, Target, ArrowRight, UserPlus, Mic, RefreshCw, Database, FileText, BarChart3, GraduationCap, BadgeCheck, Building2 } from "lucide-react";
import PresentationDownloadButton from "../components/PresentationDownloadButton";

const CALLDRIP_LOGO = "https://www.calldrip.com/hs-fs/hubfs/calldrip-logo-white.png?width=280&height=70&name=calldrip-logo-white.png";

const slides = [
  {
    id: 1,
    type: "title",
    content: {
      headline: "Fixing the First",
      highlight: "Minutes",
      subhead: "of Every Lead",
      tagline: "How to Convert More Opportunities Without Changing Your Current Stack"
    }
  },
  {
    id: 2,
    type: "problem",
    content: {
      label: "THE REALITY",
      headline: "Most Dealerships Respond",
      highlight: "Too Late",
      stats: [
        { value: "15-30", unit: "min", label: "Average Response Time" },
        { value: "3+", unit: "dealers", label: "Customers Contact Simultaneously" },
      ],
      quote: "Speed is no longer optional. It's expected."
    }
  },
  {
    id: 3,
    type: "stats",
    content: {
      label: "THE DATA",
      headline: "Speed = Connection",
      stats: [
        { value: "78%", label: "buy from first responder", color: "#22d3ee" },
        { value: "21x", label: "higher contact rate within 5 min", color: "#4ade80" },
        { value: "400%", label: "drop-off after 5 minutes", color: "#f87171" },
      ],
      quote: "This isn't opinion. It's proven behavior."
    }
  },
  {
    id: 4,
    type: "timeline",
    content: {
      label: "THE WINDOW",
      headline: "The First Minutes Matter Most",
      timeline: [
        { time: "0-2 min", status: "peak", label: "Peak Engagement", color: "#22d3ee" },
        { time: "2-5 min", status: "critical", label: "Critical Window", color: "#4ade80" },
        { time: "5-10 min", status: "declining", label: "Rapid Decline", color: "#fbbf24" },
        { time: "20+ min", status: "lost", label: "Too Late", color: "#ef4444" },
      ],
      quote: "After 20 minutes… it's already too late."
    }
  },
  {
    id: 5,
    type: "gap",
    content: {
      label: "THE GAP",
      headline: "What's Missing?",
      gaps: [
        "No immediate response at lead submission",
        "No instant call connection",
        "Missed opportunities when staff is busy",
        "No coverage nights/weekends"
      ],
      highlight: "The first minutes are unowned."
    }
  },
  {
    id: 6,
    type: "solution",
    content: {
      label: "THE SOLUTION",
      useLogo: true,
      headline: "Owns the",
      highlight: "First Minutes",
      benefits: [
        { icon: "zap", label: "Instant Response", desc: "Under 60 seconds from form submit to call" },
        { icon: "message", label: "SMS Confirmation", desc: "Customer knows you're calling" },
        { icon: "phone", label: "Live Connection", desc: "Your team patched in immediately" },
        { icon: "shield", label: "No Missed Leads", desc: "24/7 coverage with failsafe routing" },
      ],
      quote: "We create conversations instantly."
    }
  },
  {
    id: 7,
    type: "flow",
    content: {
      label: "HOW IT WORKS",
      headline: "From Lead to Conversation in Seconds",
      steps: [
        { icon: "target", label: "Lead Submits", desc: "Customer fills out form" },
        { icon: "zap", label: "Instant Alert", desc: "Rep's phone rings, customer gets text" },
        { icon: "phone", label: "Call Connects", desc: "Your team is patched in" },
        { icon: "users", label: "Live Conversation", desc: "Customer engaged" },
      ],
      quote: "While they're still on your site, we connect you."
    }
  },
  {
    id: 8,
    type: "branded",
    content: {
      label: "BRANDED CALLING",
      headline: "Your Name. Not \"Spam Likely.\"",
      phone: {
        businessName: "ABC Motors",
        tagline: "Calling you back"
      },
      points: [
        { icon: "shield", text: "Bypass spam filters with verified business identity" },
        { icon: "clock", text: "Customer sees your name within 60 seconds of form submit" },
        { icon: "trending", text: "Higher answer rates = more conversations" },
        { icon: "brand", text: "Professional first impression before they even pick up" },
      ],
      quote: "They clicked your website. Now they see your name calling back instantly."
    }
  },
  {
    id: 9,
    type: "dealersocket",
    content: {
      label: "CRM INTEGRATION",
      headline: "Seamless DealerSocket Integration",
      benefits: [
        "Seamless communication improves staff productivity",
        "Automatically logs and updates records in both systems",
        "Virtual Assistant appointments pushed directly into DealerSocket",
        "Drives accountability with improved phone insights",
        "Eliminates duplicative data entry",
        "Allows your team to devote time to working leads"
      ],
      features: [
        { label: "Logs Each New Customer", icon: "user" },
        { label: "Assigns the Dealer Agent", icon: "assign" },
        { label: "Stops the Clock on Lead Response", icon: "clock" },
        { label: "Tracks Every Call", icon: "track" },
        { label: "Links Recording to Every Call", icon: "record" },
        { label: "Updates Records Continuously", icon: "sync" },
      ],
      quote: "Your team doesn't have to change. We integrate with your existing workflow."
    }
  },
  {
    id: 10,
    type: "failsafe",
    content: {
      label: "FAILSAFE",
      headline: "No Lead Gets Ignored",
      features: [
        { icon: "retry", label: "Auto Retry", desc: "Persistent follow-up" },
        { icon: "route", label: "Smart Routing", desc: "Next available rep" },
        { icon: "fallback", label: "Fallback Logic", desc: "Always a backup" },
      ],
      quote: "Leads don't die because someone was busy."
    }
  },
  {
    id: 11,
    type: "afterhours",
    content: {
      label: "24/7 COVERAGE",
      headline: "You're Always Open",
      features: [
        "Virtual Assistant responds instantly",
        "Captures customer intent",
        "Keeps engagement alive",
        "Schedules callbacks",
        "Appointments pushed directly into DealerSocket"
      ],
      quote: "You never miss a lead again."
    }
  },
  {
    id: 12,
    type: "scorecards",
    content: {
      label: "CALL INTELLIGENCE",
      headline: "Turn Every Call Into a Coaching Opportunity",
      features: [
        { icon: "scorecard", title: "Customizable Scorecards", desc: "Set at organization or store level for consistent evaluation" },
        { icon: "voice", title: "Voice-to-Text Conversion", desc: "Every call transcribed for searchable, actionable insights" },
        { icon: "analytics", title: "True Analytics", desc: "Know exactly what's driving appointments and what isn't" },
        { icon: "training", title: "Complete KPI Visibility", desc: "Track every customized metric across your team" },
      ],
      quote: "Stop guessing. Start knowing what works."
    }
  },
  {
    id: 13,
    type: "postcall",
    content: {
      label: "AUTOMATION",
      headline: "Every Call Creates Feedback",
      items: [
        { label: "Natural Follow-Up Text", icon: "message" },
        { label: "Google Review Request", icon: "star" },
        { label: "Internal Feedback Loop", icon: "loop" },
      ],
      quote: "You improve experience automatically."
    }
  },
  {
    id: 14,
    type: "impact",
    content: {
      label: "THE IMPACT",
      headline: "What This Drives",
      metrics: [
        { label: "Faster Connections", icon: "zap" },
        { label: "More Conversations", icon: "chat" },
        { label: "More Appointments", icon: "calendar" },
        { label: "Higher Show Rates", icon: "check" },
      ],
      highlight: "Same leads. Better outcomes."
    }
  },
  {
    id: 15,
    type: "final",
    content: {
      headline: "Most dealers think they have a",
      strike: "lead problem",
      correction: "They have a response time problem.",
      cta: "Let's fix it together."
    }
  }
];

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1525 50%, #0a1020 100%)',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, sans-serif",
    position: 'relative',
    userSelect: 'none',
  },
  slide: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px 80px',
    position: 'relative',
  },
  logo: {
    position: 'absolute',
    top: '30px',
    left: '40px',
    height: '32px',
    opacity: 0.9,
  },
  slideNumber: {
    position: 'absolute',
    top: '30px',
    right: '40px',
    fontSize: '14px',
    color: 'rgba(255,255,255,0.4)',
    fontWeight: 500,
  },
  label: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#22d3ee',
    marginBottom: '16px',
  },
  headline: {
    fontSize: 'clamp(36px, 6vw, 72px)',
    fontWeight: 800,
    color: '#fff',
    textAlign: 'center',
    lineHeight: 1.1,
    marginBottom: '20px',
  },
  highlight: {
    background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  highlightRed: {
    background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subhead: {
    fontSize: 'clamp(24px, 3vw, 42px)',
    fontWeight: 300,
    color: 'rgba(255,255,255,0.7)',
    textAlign: 'center',
  },
  tagline: {
    fontSize: '18px',
    color: 'rgba(255,255,255,0.5)',
    marginTop: '40px',
    textAlign: 'center',
    maxWidth: '600px',
  },
  quote: {
    fontSize: '20px',
    fontStyle: 'italic',
    color: '#22d3ee',
    marginTop: '40px',
    padding: '20px 30px',
    borderLeft: '3px solid #22d3ee',
    background: 'rgba(34, 211, 238, 0.05)',
    borderRadius: '0 12px 12px 0',
  },
  statsGrid: {
    display: 'flex',
    gap: '40px',
    marginTop: '40px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  statCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '20px',
    padding: '30px 40px',
    textAlign: 'center',
    minWidth: '200px',
    transition: 'all 0.3s ease',
  },
  statValue: {
    fontSize: '64px',
    fontWeight: 900,
    lineHeight: 1,
  },
  statUnit: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
    marginLeft: '4px',
  },
  statLabel: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.5)',
    marginTop: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  timeline: {
    display: 'flex',
    gap: '20px',
    marginTop: '50px',
    position: 'relative',
    alignItems: 'flex-start',
  },
  timelineBar: {
    position: 'absolute',
    top: '12px',
    left: '40px',
    right: '40px',
    height: '4px',
    background: 'linear-gradient(90deg, #22d3ee, #4ade80, #fbbf24, #ef4444)',
    borderRadius: '2px',
    zIndex: 0,
  },
  timelineItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    minWidth: '150px',
    position: 'relative',
    zIndex: 1,
  },
  timelineDot: (color) => ({
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    marginBottom: '15px',
    border: '4px solid #0a0f1a',
    background: color,
    boxShadow: `0 0 20px ${color}`,
  }),
  timelineTime: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#fff',
  },
  timelineLabel: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginTop: '5px',
  },
  gapList: {
    marginTop: '40px',
    textAlign: 'left',
  },
  gapItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px 0',
    fontSize: '22px',
    color: 'rgba(255,255,255,0.8)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  gapX: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  flowContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginTop: '50px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  flowStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '25px 20px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '20px',
    width: '180px',
    minHeight: '160px',
    transition: 'all 0.3s ease',
  },
  flowIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '15px',
  },
  flowLabel: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
    textAlign: 'center',
  },
  flowDesc: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.5)',
    marginTop: '8px',
    textAlign: 'center',
    lineHeight: 1.4,
  },
  flowArrow: {
    color: 'rgba(255,255,255,0.3)',
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '25px',
    marginTop: '40px',
    maxWidth: '1000px',
  },
  benefitCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    padding: '30px 25px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
  },
  benefitIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'rgba(34,211,238,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 15px',
  },
  benefitLabel: {
    fontSize: '17px',
    fontWeight: 700,
    color: '#fff',
    marginBottom: '8px',
  },
  benefitDesc: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 1.5,
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginTop: '40px',
    maxWidth: '800px',
  },
  impactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    marginTop: '40px',
    maxWidth: '500px',
  },
  featureCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px',
    padding: '25px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
  },
  featureIcon: {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, rgba(34,211,238,0.2) 0%, rgba(6,182,212,0.2) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 15px',
    color: '#22d3ee',
  },
  checkList: {
    marginTop: '40px',
    textAlign: 'left',
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px 0',
    fontSize: '20px',
    color: 'rgba(255,255,255,0.85)',
  },
  checkIcon: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    flexShrink: 0,
  },
  finalStrike: {
    textDecoration: 'line-through',
    color: 'rgba(255,255,255,0.4)',
    fontSize: 'clamp(28px, 4vw, 48px)',
  },
  finalCorrection: {
    fontSize: 'clamp(32px, 5vw, 56px)',
    fontWeight: 800,
    color: '#22d3ee',
    marginTop: '20px',
    textAlign: 'center',
  },
  ctaButton: {
    marginTop: '50px',
    padding: '20px 50px',
    fontSize: '18px',
    fontWeight: 600,
    color: '#0a0f1a',
    background: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  navButtons: {
    position: 'absolute',
    bottom: '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
  },
  navBtn: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  navBtnDisabled: {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
  progressDots: {
    display: 'flex',
    gap: '8px',
  },
  progressDot: (active) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: active ? '#22d3ee' : 'rgba(255,255,255,0.2)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    transform: active ? 'scale(1.3)' : 'scale(1)',
  }),
};

export default function Presentations() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = useCallback((index, dir) => {
    if (isAnimating || index < 0 || index >= slides.length) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsAnimating(false);
    }, 100);
  }, [isAnimating]);

  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      goToSlide(currentSlide + 1, 1);
    }
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1, -1);
    }
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  useEffect(() => {
    let startX = 0;
    const handleTouchStart = (e) => { startX = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) nextSlide();
        else prevSlide();
      }
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [nextSlide, prevSlide]);

  const slide = slides[currentSlide];

  const renderSlideContent = (slide) => {
    switch (slide.type) {
      case "title":
        return (
          <>
            <h1 style={styles.headline}>
              {slide.content.headline} <span style={styles.highlight}>{slide.content.highlight}</span>
            </h1>
            <p style={styles.subhead}>{slide.content.subhead}</p>
            <p style={styles.tagline}>{slide.content.tagline}</p>
          </>
        );
      
      case "problem":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={styles.headline}>
              {slide.content.headline} <span style={styles.highlightRed}>{slide.content.highlight}</span>
            </h1>
            <div style={styles.statsGrid}>
              {slide.content.stats.map((stat, i) => (
                <div style={styles.statCard} key={i}>
                  <div style={{...styles.statValue, color: '#f87171'}}>{stat.value}<span style={styles.statUnit}>{stat.unit}</span></div>
                  <div style={styles.statLabel}>{stat.label}</div>
                </div>
              ))}
            </div>
            <div style={styles.quote}>{slide.content.quote}</div>
          </>
        );

      case "stats":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={styles.headline}>{slide.content.headline}</h1>
            <div style={styles.statsGrid}>
              {slide.content.stats.map((stat, i) => (
                <div style={styles.statCard} key={i}>
                  <div style={{...styles.statValue, color: stat.color}}>{stat.value}</div>
                  <div style={styles.statLabel}>{stat.label}</div>
                </div>
              ))}
            </div>
            <div style={styles.quote}>{slide.content.quote}</div>
          </>
        );

      case "timeline":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={styles.headline}>{slide.content.headline}</h1>
            <div style={styles.timeline}>
              <div style={styles.timelineBar}></div>
              {slide.content.timeline.map((item, i) => (
                <div style={styles.timelineItem} key={i}>
                  <div style={styles.timelineDot(item.color)}></div>
                  <div style={styles.timelineTime}>{item.time}</div>
                  <div style={styles.timelineLabel}>{item.label}</div>
                </div>
              ))}
            </div>
            <div style={styles.quote}>{slide.content.quote}</div>
          </>
        );

      case "gap":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={styles.headline}>{slide.content.headline}</h1>
            <div style={styles.gapList}>
              {slide.content.gaps.map((gap, i) => (
                <div style={styles.gapItem} key={i}>
                  <div style={styles.gapX}>✕</div>
                  {gap}
                </div>
              ))}
            </div>
            <div style={{...styles.quote, borderColor: '#ef4444', background: 'rgba(239,68,68,0.05)', color: '#ef4444'}}>
              {slide.content.highlight}
            </div>
          </>
        );

      case "solution":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={{...styles.headline, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px'}}>
              {slide.content.useLogo && (
                <img src={CALLDRIP_LOGO} alt="Calldrip" style={{ height: '65px' }} />
              )}
              <span>{slide.content.headline} <span style={styles.highlight}>{slide.content.highlight}</span></span>
            </h1>
            <div style={styles.benefitsGrid}>
              {slide.content.benefits.map((benefit, i) => (
                <div key={i} style={styles.benefitCard}>
                  <div style={styles.benefitIcon}>
                    {benefit.icon === 'zap' && <Zap size={28} color="#22d3ee" />}
                    {benefit.icon === 'message' && <MessageSquare size={28} color="#22d3ee" />}
                    {benefit.icon === 'phone' && <Phone size={28} color="#22d3ee" />}
                    {benefit.icon === 'shield' && <Shield size={28} color="#22d3ee" />}
                  </div>
                  <div style={styles.benefitLabel}>{benefit.label}</div>
                  <div style={styles.benefitDesc}>{benefit.desc}</div>
                </div>
              ))}
            </div>
            <div style={styles.quote}>{slide.content.quote}</div>
          </>
        );

      case "flow":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={{...styles.headline, fontSize: 'clamp(28px, 4vw, 48px)'}}>{slide.content.headline}</h1>
            <div style={styles.flowContainer}>
              {slide.content.steps.map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={styles.flowStep}>
                    <div style={styles.flowIcon}>
                      {step.icon === 'target' && <Target size={28} color="#fff" />}
                      {step.icon === 'zap' && <Zap size={28} color="#fff" />}
                      {step.icon === 'phone' && <Phone size={28} color="#fff" />}
                      {step.icon === 'users' && <Users size={28} color="#fff" />}
                    </div>
                    <div style={styles.flowLabel}>{step.label}</div>
                    <div style={styles.flowDesc}>{step.desc}</div>
                  </div>
                  {i < slide.content.steps.length - 1 && (
                    <ArrowRight size={20} style={styles.flowArrow} />
                  )}
                </div>
              ))}
            </div>
            <div style={styles.quote}>{slide.content.quote}</div>
          </>
        );

      case "branded":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={{...styles.headline, fontSize: 'clamp(28px, 4.5vw, 56px)'}}>{slide.content.headline}</h1>
            <div style={{ display: 'flex', gap: '60px', marginTop: '40px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              {/* Phone mockup */}
              <div style={{ 
                width: '280px', 
                background: 'linear-gradient(180deg, #1a1a2e 0%, #16162a 100%)', 
                borderRadius: '36px', 
                padding: '12px',
                boxShadow: '0 25px 80px rgba(0,0,0,0.5), inset 0 1px 1px rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{ 
                  background: '#000', 
                  borderRadius: '28px', 
                  padding: '40px 20px',
                  minHeight: '320px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    boxShadow: '0 0 40px rgba(74, 222, 128, 0.4)'
                  }}>
                    <Phone size={36} color="#fff" />
                  </div>
                  <div style={{ 
                    background: 'linear-gradient(135deg, rgba(74,222,128,0.15) 0%, rgba(34,197,94,0.1) 100%)',
                    border: '1px solid rgba(74,222,128,0.3)',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    textAlign: 'center',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
                      <BadgeCheck size={18} color="#4ade80" />
                      <span style={{ color: '#4ade80', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Verified Business</span>
                    </div>
                    <div style={{ color: '#fff', fontSize: '22px', fontWeight: 700 }}>{slide.content.phone.businessName}</div>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>{slide.content.phone.tagline}</div>
                  <div style={{ display: 'flex', gap: '40px', marginTop: '30px' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Phone size={24} color="#fff" style={{ transform: 'rotate(135deg)' }} />
                    </div>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Phone size={24} color="#fff" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Benefits list */}
              <div style={{ maxWidth: '400px' }}>
                {slide.content.points.map((point, i) => (
                  <div key={i} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '16px', 
                    padding: '16px 0',
                    borderBottom: i < slide.content.points.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none'
                  }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '10px', 
                      background: 'rgba(74, 222, 128, 0.1)',
                      border: '1px solid rgba(74, 222, 128, 0.2)',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {point.icon === 'shield' && <Shield size={20} color="#4ade80" />}
                      {point.icon === 'clock' && <Clock size={20} color="#4ade80" />}
                      {point.icon === 'trending' && <TrendingUp size={20} color="#4ade80" />}
                      {point.icon === 'brand' && <Building2 size={20} color="#4ade80" />}
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '16px', lineHeight: 1.5, margin: 0 }}>{point.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{...styles.quote, borderColor: '#4ade80', background: 'rgba(74,222,128,0.05)', color: '#4ade80', marginTop: '35px'}}>
              {slide.content.quote}
            </div>
          </>
        );

      case "dealersocket":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={{...styles.headline, fontSize: 'clamp(24px, 3.5vw, 44px)'}}>{slide.content.headline}</h1>
            <div style={{ display: 'flex', gap: '40px', marginTop: '30px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '1200px' }}>
              {/* Benefits Column */}
              <div style={{ flex: '1', minWidth: '300px', maxWidth: '450px' }}>
                <h3 style={{ color: '#4ade80', fontSize: '14px', fontWeight: 700, letterSpacing: '2px', marginBottom: '20px', textTransform: 'uppercase' }}>Benefits</h3>
                {slide.content.benefits.map((benefit, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <CheckCircle size={18} color="#4ade80" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '15px', lineHeight: 1.4 }}>{benefit}</span>
                  </div>
                ))}
              </div>
              {/* Features Column */}
              <div style={{ flex: '1', minWidth: '300px', maxWidth: '500px' }}>
                <h3 style={{ color: '#22d3ee', fontSize: '14px', fontWeight: 700, letterSpacing: '2px', marginBottom: '20px', textTransform: 'uppercase' }}>What It Does</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  {slide.content.features.map((f, i) => (
                    <div key={i} style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.2)', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(34,211,238,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {f.icon === 'user' && <UserPlus size={18} color="#22d3ee" />}
                        {f.icon === 'assign' && <Users size={18} color="#22d3ee" />}
                        {f.icon === 'clock' && <Clock size={18} color="#22d3ee" />}
                        {f.icon === 'track' && <Target size={18} color="#22d3ee" />}
                        {f.icon === 'record' && <Mic size={18} color="#22d3ee" />}
                        {f.icon === 'sync' && <RefreshCw size={18} color="#22d3ee" />}
                      </div>
                      <span style={{ color: '#fff', fontSize: '13px', fontWeight: 500 }}>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{...styles.quote, marginTop: '30px'}}>{slide.content.quote}</div>
          </>
        );

      case "failsafe":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={styles.headline}>{slide.content.headline}</h1>
            <div style={styles.featureGrid}>
              {slide.content.features.map((f, i) => (
                <div style={styles.featureCard} key={i}>
                  <div style={styles.featureIcon}>
                    {f.icon === 'retry' && <TrendingUp size={24} />}
                    {f.icon === 'route' && <Users size={24} />}
                    {f.icon === 'fallback' && <Shield size={24} />}
                  </div>
                  <div style={styles.flowLabel}>{f.label}</div>
                  <div style={styles.flowDesc}>{f.desc}</div>
                </div>
              ))}
            </div>
            <div style={styles.quote}>{slide.content.quote}</div>
          </>
        );

      case "afterhours":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={styles.headline}>{slide.content.headline}</h1>
            <div style={styles.checkList}>
              {slide.content.features.map((f, i) => (
                <div style={styles.checkItem} key={i}>
                  <div style={styles.checkIcon}><CheckCircle size={16} /></div>
                  {f}
                </div>
              ))}
            </div>
            <div style={styles.quote}>{slide.content.quote}</div>
          </>
        );

      case "scorecards":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={{...styles.headline, fontSize: 'clamp(24px, 3.5vw, 44px)'}}>{slide.content.headline}</h1>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '40px', maxWidth: '900px' }}>
              {slide.content.features.map((f, i) => (
                <div key={i} style={{ 
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)', 
                  border: '1px solid rgba(139, 92, 246, 0.3)', 
                  borderRadius: '16px', 
                  padding: '24px',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '12px', 
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {f.icon === 'scorecard' && <FileText size={24} color="#fff" />}
                      {f.icon === 'voice' && <Mic size={24} color="#fff" />}
                      {f.icon === 'analytics' && <BarChart3 size={24} color="#fff" />}
                      {f.icon === 'training' && <GraduationCap size={24} color="#fff" />}
                    </div>
                    <div>
                      <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: 600, marginBottom: '6px' }}>{f.title}</h3>
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1.5 }}>{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{...styles.quote, borderColor: '#8b5cf6', background: 'rgba(139, 92, 246, 0.05)', color: '#a78bfa', marginTop: '35px'}}>
              {slide.content.quote}
            </div>
          </>
        );

      case "postcall":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={styles.headline}>{slide.content.headline}</h1>
            <div style={styles.featureGrid}>
              {slide.content.items.map((item, i) => (
                <div style={styles.featureCard} key={i}>
                  <div style={styles.featureIcon}>
                    {item.icon === 'message' && <MessageSquare size={24} />}
                    {item.icon === 'star' && <Target size={24} />}
                    {item.icon === 'loop' && <TrendingUp size={24} />}
                  </div>
                  <div style={styles.flowLabel}>{item.label}</div>
                </div>
              ))}
            </div>
            <div style={styles.quote}>{slide.content.quote}</div>
          </>
        );

      case "impact":
        return (
          <>
            <div style={styles.label}>{slide.content.label}</div>
            <h1 style={styles.headline}>{slide.content.headline}</h1>
            <div style={styles.impactGrid}>
              {slide.content.metrics.map((m, i) => (
                <div style={styles.featureCard} key={i}>
                  <div style={styles.featureIcon}>
                    {m.icon === 'zap' && <Zap size={24} />}
                    {m.icon === 'chat' && <MessageSquare size={24} />}
                    {m.icon === 'calendar' && <Clock size={24} />}
                    {m.icon === 'check' && <CheckCircle size={24} />}
                  </div>
                  <div style={styles.flowLabel}>{m.label}</div>
                </div>
              ))}
            </div>
            <div style={{...styles.quote, borderColor: '#4ade80', background: 'rgba(74,222,128,0.05)', color: '#4ade80'}}>
              {slide.content.highlight}
            </div>
          </>
        );

      case "final":
        return (
          <>
            <h1 style={{...styles.headline, fontSize: 'clamp(24px, 3.5vw, 40px)', fontWeight: 400}}>
              {slide.content.headline}
            </h1>
            <div style={styles.finalStrike}>{slide.content.strike}</div>
            <div style={styles.finalCorrection}>{slide.content.correction}</div>
            <a href="https://calldrip.com/demo" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
              <button style={styles.ctaButton}>{slide.content.cta}</button>
            </a>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      
      <img src={CALLDRIP_LOGO} alt="Calldrip" style={styles.logo} />
      <div style={styles.slideNumber}>{currentSlide + 1} / {slides.length}</div>

      <div style={styles.slide} key={slide.id}>
        {renderSlideContent(slide)}
      </div>

      {/* Download PDF button (floating top-right) */}
      <div style={{ position: "fixed", top: "20px", right: "120px", zIndex: 100 }}>
        <PresentationDownloadButton
          slides={slides}
          renderSlide={(s) => (
            <div style={{ ...styles.container, width: "1920px", height: "1080px", position: "relative" }}>
              <img src={CALLDRIP_LOGO} alt="Calldrip" style={styles.logo} />
              <div style={styles.slide}>{renderSlideContent(s)}</div>
            </div>
          )}
          filename="Calldrip-Blue-Deck"
          bgColor="#0a0f1a"
        />
      </div>

      <div style={styles.navButtons}>
        <button 
          style={{...styles.navBtn, ...(currentSlide === 0 ? styles.navBtnDisabled : {})}} 
          onClick={prevSlide} 
          disabled={currentSlide === 0}
        >
          <ChevronLeft size={24} />
        </button>
        
        <div style={styles.progressDots}>
          {slides.map((_, i) => (
            <div
              key={i}
              style={styles.progressDot(i === currentSlide)}
              onClick={() => goToSlide(i, i > currentSlide ? 1 : -1)}
            />
          ))}
        </div>
        
        <button 
          style={{...styles.navBtn, ...(currentSlide === slides.length - 1 ? styles.navBtnDisabled : {})}} 
          onClick={nextSlide} 
          disabled={currentSlide === slides.length - 1}
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
}
